/**
 * Rock model builders — ported from design_sheets/rocks.html.
 * Deterministic models via seeded RNG. Three rock types: basalt, alabaster, sandstone.
 * Five modes: small, medium, large, cluster, outcrop.
 */

import * as THREE from 'three';
import type { RockType, RockMode } from '../types';
import { ROCK_PALETTES } from '../constants';

// Seeded pseudo-random for deterministic rock shapes
function seededRand(seed: number): () => number {
  let s = seed;
  return function () {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function mat(c: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: new THREE.Color(c) });
}

function matFlat(c: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: new THREE.Color(c), flatShading: true });
}

// =============================================================================
// BASALT — hex column clusters
// =============================================================================

function buildBasaltRock(size: 'small' | 'medium' | 'large', scale: number, seed: number): THREE.Group {
  const g = new THREE.Group();
  const rng = seededRand(seed);
  const s = (size === 'large' ? 1.0 : size === 'medium' ? 0.6 : 0.3) * scale;
  const pal = ROCK_PALETTES.basalt;

  const numCols = size === 'large' ? 7 : size === 'medium' ? 4 : 2;
  const colR = 0.055 * s;

  const positions: [number, number][] = [];
  if (numCols >= 7) {
    positions.push([0, 0]);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.05;
      positions.push([Math.cos(a) * colR * 2, Math.sin(a) * colR * 2]);
    }
  } else if (numCols >= 4) {
    positions.push([0, 0]);
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + rng() * 0.3;
      positions.push([Math.cos(a) * colR * 2, Math.sin(a) * colR * 2]);
    }
  } else {
    positions.push([0, 0]);
    positions.push([colR * 1.6, colR * 0.5]);
  }

  const colors = [pal.base, pal.mid, pal.light];
  for (let i = 0; i < positions.length; i++) {
    const px = positions[i][0];
    const pz = positions[i][1];
    const h = (0.15 + rng() * 0.25) * s;
    const r = colR * (0.85 + rng() * 0.3);

    const col = new THREE.Mesh(
      new THREE.CylinderGeometry(r, r * (0.95 + rng() * 0.1), h, 6),
      matFlat(colors[i % 3])
    );
    col.position.set(px, h / 2, pz);
    col.rotation.x = (rng() - 0.5) * 0.08;
    col.rotation.z = (rng() - 0.5) * 0.08;
    g.add(col);

    const cap = new THREE.Mesh(
      new THREE.CircleGeometry(r * 0.95, 6),
      matFlat(pal.mid)
    );
    cap.position.set(px, h + 0.001, pz);
    cap.rotation.x = -Math.PI / 2;
    cap.rotation.z = col.rotation.z;
    g.add(cap);
  }

  return g;
}

// =============================================================================
// ALABASTER — stacked beveled polygon slabs
// =============================================================================

function buildAlabasterRock(size: 'small' | 'medium' | 'large', scale: number, seed: number): THREE.Group {
  const g = new THREE.Group();
  const rng = seededRand(seed);
  const s = (size === 'large' ? 1.0 : size === 'medium' ? 0.6 : 0.3) * scale;
  const pal = ROCK_PALETTES.alabaster;

  const numSlabs = size === 'large' ? 3 : size === 'medium' ? 2 : 1;
  let y = 0;

  const colors = [pal.base, pal.light, pal.mid];
  for (let i = 0; i < numSlabs; i++) {
    const t = i / Math.max(numSlabs - 1, 1);
    const baseR = (0.18 - t * 0.04) * s;
    const h = (0.06 + rng() * 0.03) * s;

    const segs = 6 + Math.floor(rng() * 3);
    const shape = new THREE.Shape();
    for (let j = 0; j <= segs; j++) {
      const a = (j / segs) * Math.PI * 2;
      const r = baseR * (0.65 + rng() * 0.7);
      const px = Math.cos(a) * r;
      const pz = Math.sin(a) * r;
      if (j === 0) shape.moveTo(px, pz);
      else shape.lineTo(px, pz);
    }

    const slabGeo = new THREE.ExtrudeGeometry(shape, {
      depth: h,
      bevelEnabled: true,
      bevelThickness: h * 0.3,
      bevelSize: baseR * 0.15,
      bevelSegments: 3,
      curveSegments: segs,
    });

    const slab = new THREE.Mesh(slabGeo, mat(colors[i % 3]));
    slab.rotation.x = -Math.PI / 2;
    slab.position.set(
      (rng() - 0.5) * 0.04 * s,
      y + h * 0.3,
      (rng() - 0.5) * 0.04 * s
    );
    slab.rotation.z = (rng() - 0.5) * 0.4;
    slab.rotation.y = (rng() - 0.5) * 0.08;
    g.add(slab);

    y += h * 1.05;
  }

  // Offset chip for medium/large
  if (size !== 'small') {
    const chipR = 0.07 * s;
    const chipH = 0.03 * s;
    const chipSegs = 5;
    const chipShape = new THREE.Shape();
    for (let j = 0; j <= chipSegs; j++) {
      const a = (j / chipSegs) * Math.PI * 2;
      const r = chipR * (0.6 + rng() * 0.8);
      if (j === 0) chipShape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      else chipShape.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    const chipGeo = new THREE.ExtrudeGeometry(chipShape, {
      depth: chipH,
      bevelEnabled: true,
      bevelThickness: chipH * 0.35,
      bevelSize: chipR * 0.15,
      bevelSegments: 2,
      curveSegments: chipSegs,
    });
    const chip = new THREE.Mesh(chipGeo, mat(pal.crack));
    chip.rotation.x = -Math.PI / 2;
    const chipAngle = rng() * Math.PI * 2;
    chip.position.set(
      Math.cos(chipAngle) * 0.15 * s,
      0.01,
      Math.sin(chipAngle) * 0.12 * s
    );
    chip.rotation.z = rng() * Math.PI;
    chip.rotation.y = (rng() - 0.5) * 0.2;
    g.add(chip);
  }

  return g;
}

// =============================================================================
// SANDSTONE — rounded eroded slab stacks
// =============================================================================

function buildSandstoneRock(size: 'small' | 'medium' | 'large', scale: number, seed: number): THREE.Group {
  const g = new THREE.Group();
  const rng = seededRand(seed);
  const s = (size === 'large' ? 1.0 : size === 'medium' ? 0.6 : 0.3) * scale;
  const pal = ROCK_PALETTES.sandstone;

  const numSlabs = size === 'large' ? 4 : size === 'medium' ? 3 : 2;
  let y = 0;

  const colors = [pal.base, pal.light, pal.mid];
  for (let i = 0; i < numSlabs; i++) {
    const t = i / Math.max(numSlabs - 1, 1);
    const baseR = (0.18 - t * 0.06) * s;
    const h = (0.03 + rng() * 0.025) * s;

    const segs = 10;
    const shape = new THREE.Shape();
    for (let j = 0; j <= segs; j++) {
      const a = (j / segs) * Math.PI * 2;
      const r = baseR * (0.7 + rng() * 0.6);
      const px = Math.cos(a) * r;
      const pz = Math.sin(a) * r;
      if (j === 0) shape.moveTo(px, pz);
      else shape.lineTo(px, pz);
    }

    const slabGeo = new THREE.ExtrudeGeometry(shape, {
      depth: h,
      bevelEnabled: true,
      bevelThickness: h * 0.4,
      bevelSize: baseR * 0.12,
      bevelSegments: 2,
      curveSegments: segs,
    });

    const slab = new THREE.Mesh(slabGeo, mat(colors[i % 3]));
    slab.rotation.x = -Math.PI / 2;
    slab.position.set(
      (rng() - 0.5) * 0.025 * s,
      y + h * 0.4,
      (rng() - 0.5) * 0.025 * s
    );
    slab.rotation.z = (rng() - 0.5) * 0.15;
    g.add(slab);

    y += h * 1.1;
  }

  // Dust skirt
  const dust = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18 * s, 0.22 * s, 0.008 * s, 10),
    new THREE.MeshLambertMaterial({
      color: new THREE.Color(pal.mid),
      transparent: true,
      opacity: 0.25,
    })
  );
  dust.position.y = 0.004;
  g.add(dust);

  return g;
}

// =============================================================================
// CLUSTER — 1 large + 2-3 medium + 3-4 small scattered
// =============================================================================

function buildRockCluster(rockType: RockType, scale: number, seed: number): THREE.Group {
  const g = new THREE.Group();
  const builder = rockType === 'basalt' ? buildBasaltRock : rockType === 'alabaster' ? buildAlabasterRock : buildSandstoneRock;
  const rng = seededRand(seed);

  const big = builder('large', scale, seed + 42);
  g.add(big);

  const numMed = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < numMed; i++) {
    const med = builder('medium', scale, seed + 1000 + i * 777);
    const a = (i / numMed) * Math.PI * 2 + rng() * 1.0;
    const r = (0.25 + rng() * 0.2) * scale;
    med.position.set(Math.cos(a) * r, 0, Math.sin(a) * r);
    med.rotation.y = rng() * Math.PI * 2;
    const sv = 0.8 + rng() * 0.5;
    med.scale.set(sv, 0.7 + rng() * 0.6, sv);
    g.add(med);
  }

  const numSm = 3 + Math.floor(rng() * 2);
  for (let i = 0; i < numSm; i++) {
    const sm = builder('small', scale, seed + 5000 + i * 1234);
    const a = rng() * Math.PI * 2;
    const r = (0.15 + rng() * 0.5) * scale;
    sm.position.set(Math.cos(a) * r, 0, Math.sin(a) * r);
    sm.rotation.y = rng() * Math.PI * 2;
    const sv2 = 0.7 + rng() * 0.6;
    sm.scale.set(sv2, 0.6 + rng() * 0.8, sv2);
    g.add(sm);
  }

  return g;
}

// =============================================================================
// OUTCROP — tall vertical formation
// =============================================================================

function buildRockOutcrop(rockType: RockType, scale: number, seed: number): THREE.Group {
  const g = new THREE.Group();
  const rng = seededRand(seed);

  if (rockType === 'basalt') {
    const pal = ROCK_PALETTES.basalt;
    const colR = (0.04 + rng() * 0.015) * scale;
    const numCols = 5 + Math.floor(rng() * 3);

    const positions: [number, number][] = [[0, 0]];
    for (let i = 1; i < numCols; i++) {
      const a = (i / (numCols - 1)) * Math.PI * 2 + rng() * 0.8;
      const r = colR * (1.8 + rng() * 0.5);
      positions.push([Math.cos(a) * r, Math.sin(a) * r]);
    }

    const colors = [pal.base, pal.mid, pal.light];
    for (let i = 0; i < positions.length; i++) {
      const ch = (0.3 + rng() * 0.3) * scale;
      const cr = colR * (0.85 + rng() * 0.3);
      const col = new THREE.Mesh(
        new THREE.CylinderGeometry(cr, cr * 1.02, ch, 6),
        matFlat(colors[i % 3])
      );
      col.position.set(positions[i][0], ch / 2, positions[i][1]);
      col.rotation.x = (rng() - 0.5) * 0.06;
      col.rotation.z = (rng() - 0.5) * 0.06;
      g.add(col);

      const cap = new THREE.Mesh(
        new THREE.CircleGeometry(cr * 0.93, 6),
        matFlat(pal.mid)
      );
      cap.position.set(positions[i][0], ch + 0.001, positions[i][1]);
      cap.rotation.x = -Math.PI / 2;
      g.add(cap);
    }

  } else if (rockType === 'alabaster') {
    const pal = ROCK_PALETTES.alabaster;
    const numSlabs = 4 + Math.floor(rng() * 3);
    let y = 0;
    let driftX = 0, driftZ = 0;
    const colors = [pal.light, pal.base, pal.mid];

    for (let i = 0; i < numSlabs; i++) {
      const baseR = (0.1 + rng() * 0.1) * scale;
      const h = (0.03 + rng() * 0.05) * scale;
      const segs = 5 + Math.floor(rng() * 4);
      const shape = new THREE.Shape();
      for (let j = 0; j <= segs; j++) {
        const a = (j / segs) * Math.PI * 2;
        const r = baseR * (0.55 + rng() * 0.9);
        if (j === 0) shape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else shape.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      const slabGeo = new THREE.ExtrudeGeometry(shape, {
        depth: h, bevelEnabled: true,
        bevelThickness: h * 0.28, bevelSize: baseR * 0.12,
        bevelSegments: 3, curveSegments: segs,
      });
      const slab = new THREE.Mesh(slabGeo, mat(colors[i % 3]));
      slab.rotation.x = -Math.PI / 2;
      driftX += (rng() - 0.5) * 0.04 * scale;
      driftZ += (rng() - 0.5) * 0.04 * scale;
      slab.position.set(driftX, y, driftZ);
      slab.rotation.z = (rng() - 0.5) * 0.5;
      slab.rotation.y = (rng() - 0.5) * 0.1;
      g.add(slab);

      y += h * (0.8 + rng() * 0.5);
    }

  } else {
    // Sandstone tower
    const pal = ROCK_PALETTES.sandstone;
    const numSlabs = 6 + Math.floor(rng() * 4);
    let y = 0;
    const colors = [pal.base, pal.light, pal.mid];

    for (let i = 0; i < numSlabs; i++) {
      const t = i / (numSlabs - 1);
      const baseR = (0.14 - t * 0.06 + (rng() - 0.5) * 0.025) * scale;
      const h = (0.025 + rng() * 0.02) * scale;
      const segs = 10;
      const shape = new THREE.Shape();
      for (let j = 0; j <= segs; j++) {
        const a = (j / segs) * Math.PI * 2;
        const r = baseR * (0.7 + rng() * 0.6);
        if (j === 0) shape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else shape.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      const slabGeo = new THREE.ExtrudeGeometry(shape, {
        depth: h, bevelEnabled: true,
        bevelThickness: h * 0.35, bevelSize: baseR * 0.1,
        bevelSegments: 2, curveSegments: segs,
      });
      const slab = new THREE.Mesh(slabGeo, mat(colors[i % 3]));
      slab.rotation.x = -Math.PI / 2;
      slab.position.set(
        (rng() - 0.5) * 0.012 * scale,
        y + h * 0.35,
        (rng() - 0.5) * 0.012 * scale
      );
      slab.rotation.z = (rng() - 0.5) * 0.12;
      g.add(slab);
      y += h * 1.0;
    }

    // Dust skirt
    const dust = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12 * scale, 0.16 * scale, 0.008 * scale, 10),
      new THREE.MeshLambertMaterial({
        color: new THREE.Color(pal.mid),
        transparent: true,
        opacity: 0.2,
      })
    );
    dust.position.y = 0.004;
    g.add(dust);
  }

  return g;
}

// =============================================================================
// DISPATCHER
// =============================================================================

export function buildRockModel(rockType: RockType, mode: RockMode, scale: number, seed: number): THREE.Group {
  if (mode === 'cluster') return buildRockCluster(rockType, scale, seed);
  if (mode === 'outcrop') return buildRockOutcrop(rockType, scale, seed);
  const builder = rockType === 'basalt' ? buildBasaltRock : rockType === 'alabaster' ? buildAlabasterRock : buildSandstoneRock;
  return builder(mode as 'small' | 'medium' | 'large', scale, seed);
}
